'use client';

/**
 * Hook for connecting a Google account to the current user —
 * relocated from the frontend into the SDK (auth group D).
 *
 * Lives in `src/platform/web/auth/` because it imports
 * `@turnkey/react-wallet-kit` (`useTurnkey`, `addOauthProvider`) —
 * a web-only vendor SDK. The RN equivalent would live in
 * `src/platform/react-native/auth/`.
 *
 * App-specific concerns (toast, logger, analytics, Next.js routing)
 * are NOT embedded here. Callers supply them via the `deps` argument:
 *
 *   - `connectProvider`: the app's backend mutation (e.g.
 *     `useConnectProvider().connectProvider` from the frontend queries)
 *   - `user`: the current auth user (email, etc.)
 *   - `setUser`: update local user state after profile sync
 *   - `updateProfile`: async fn that PATCHes the user's profile
 *
 * Callers own toast / error surfacing after each returned result.
 *
 * Encapsulates the full flow:
 * 1. Google OAuth popup → captures raw OIDC token (with email)
 *    (or same-tab redirect fallback when popups are blocked — see useOauthConnect)
 * 2. Email validation (must match account email if one exists)
 * 3. Turnkey registration (addOauthProvider — programmatic, no OTP modal)
 * 4. Morfi DB registration (connectProvider callback)
 * 5. Email sync to user profile (updateProfile + setUser callbacks)
 */
import { useTurnkey } from '@turnkey/react-wallet-kit';
import { useCallback } from 'react';
import { decodeJwtPayload, turnkeyAlreadyHoldsIdentity, validateGoogleEmailMatch } from '../../../../auth/oauth/oauth-claims.js';
import { useOauthConnect } from './use-oauth-connect.js';
export function useGoogleConnect(deps) {
  const {
    addOauthProvider,
    user: turnkeyUser
  } = useTurnkey();
  const {
    runOauthConnect
  } = useOauthConnect();
  const {
    user,
    setUser,
    connectProvider,
    updateProfile
  } = deps;
  const finalizeGoogleConnectViaTurnkey = useCallback(async providerUserId => {
    try {
      // Trust-Turnkey mode: Turnkey already added Google to the user's
      // sub-org via handleAddOauthProvider. Backend creates the DB row
      // tied to the authenticated Morfi session.
      await connectProvider({
        provider: 'google',
        importProfile: false,
        ...(providerUserId !== undefined && providerUserId !== '' ? {
          providerUserId
        } : {})
      });
    } catch (dbErr) {
      // SILENT-CATCH: cross-user collisions rethrow with intentional toast
      // dismissal; non-collision failures are swallowed because the
      // trust-Turnkey path is best-effort.
      const dbMsg = dbErr instanceof Error ? dbErr.message : String(dbErr);
      if (dbMsg.includes('ALREADY_LINKED') || dbMsg.includes('already linked') || dbMsg.includes('PROVIDER_TYPE_ALREADY_CONNECTED') || dbMsg.includes('already have')) {
        throw dbErr;
      }
      // Non-collision failure — swallow (best-effort path)
    }
    return {
      success: true,
      email: null,
      mismatchEmail: null,
      redirecting: false
    };
  }, [connectProvider]);
  const finalizeGoogleConnect = useCallback(async oidcToken => {
    const claims = decodeJwtPayload(oidcToken);
    const capturedEmail = typeof claims.email === 'string' ? claims.email : null;
    const emailCheck = validateGoogleEmailMatch(user?.email, capturedEmail);
    if (!emailCheck.allowed) {
      return {
        success: false,
        email: null,
        mismatchEmail: emailCheck.accountEmail,
        redirecting: false
      };
    }
    // The Turnkey registration is the link: it is what lets a later Google
    // sign-in land on the sub-org that holds the wallets, and the backend
    // refuses the provider row without it. Turnkey rejects an identity
    // another sub-org already holds ("Account already exists"), which the
    // caller shows as linked-elsewhere; any failure here ends the connect.
    // When this sub-org already holds the identity (the redirect flow
    // registered it through `handleAddOauthProvider` before this finalize
    // ran, or an earlier attempt registered it and the row write failed)
    // a second add would be that same rejection, so it is skipped.
    if (!turnkeyAlreadyHoldsIdentity(turnkeyUser?.oauthProviders ?? [], 'google', oidcToken)) {
      await addOauthProvider({
        providerName: 'google',
        oidcToken
      });
    }
    try {
      const oauthProfile = {};
      if (capturedEmail !== null && capturedEmail !== '') oauthProfile.email = capturedEmail;
      await connectProvider({
        oidcToken,
        provider: 'google',
        importProfile: false,
        oauthProfile
      });
    } catch (dbErr_0) {
      // Re-throw "already linked" so caller shows the right toast; log other
      // failures (best-effort — next Google login auto-creates the row).
      const dbMsg_0 = dbErr_0 instanceof Error ? dbErr_0.message : String(dbErr_0);
      const isAlreadyLinked = dbMsg_0.includes('ALREADY_LINKED') || dbMsg_0.includes('already linked') || dbMsg_0.includes('PROVIDER_TYPE_ALREADY_CONNECTED') || dbMsg_0.includes('already have');
      if (isAlreadyLinked) {
        throw dbErr_0;
      }
      // Non-fatal — continue (next Google login auto-creates the row)
    }
    if (capturedEmail !== null && capturedEmail !== '' && user !== null) {
      // ALLOW-UNKNOWN: spreading TUser and adding email produces a structurally
      // compatible TUser & { email: string } value — the cast is safe at the
      // dep-injection boundary where caller controls TUser's concrete shape.
      const userWithEmail = {
        ...user,
        email: capturedEmail
      };
      try {
        // IMPERSONATION_GUARD: Google OAuth callback runs during engineer's own auth flow — Turnkey signing requires the engineer's keys, which impersonation cannot supply. Backend 403s as defence in depth.
        await updateProfile({
          email: capturedEmail
        });
        setUser(userWithEmail);
      } catch {
        // SILENT-CATCH: backend update failed — keep local state in sync anyway
        setUser(userWithEmail);
      }
    }
    return {
      success: true,
      email: capturedEmail,
      mismatchEmail: null,
      redirecting: false
    };
  }, [addOauthProvider, turnkeyUser?.oauthProviders, connectProvider, user, setUser, updateProfile]);
  const connectGoogle = useCallback(async () => {
    const result = await runOauthConnect({
      provider: 'google'
    });
    if (result.kind === 'redirecting') {
      return {
        success: false,
        email: null,
        mismatchEmail: null,
        redirecting: true
      };
    }
    if (result.kind === 'cancelled') {
      return {
        success: false,
        email: null,
        mismatchEmail: null,
        redirecting: false
      };
    }
    if (result.kind === 'error') {
      throw result.error;
    }
    return finalizeGoogleConnect(result.oidcToken);
  }, [runOauthConnect, finalizeGoogleConnect]);
  return {
    connectGoogle,
    finalizeGoogleConnect,
    finalizeGoogleConnectViaTurnkey
  };
}